/**
 * OfficeRND auto-login URL-param consumer.
 *
 * The OfficeRND iframe loads Concierge with the member's JWT in the URL
 * query string. Leaving it there leaks into history, server access logs,
 * and Referer headers. This helper:
 *
 *   1. reads memberId / memberName / token from window.location.search,
 *   2. mirrors them into sessionStorage (per-tab, so tab-internal
 *      reloads — required by the no-logout iframe flow — still see them),
 *   3. scrubs the params from the URL via history.replaceState.
 *
 * Why sessionStorage rather than keeping the URL:
 *   The token must survive `window.location.reload()` triggered by the
 *   sessionExpired handler in AuthContext / TokenRefreshManager (the
 *   OfficeRND iframe has no logout button, so reload-and-re-auth is the
 *   only recovery path on token expiry). sessionStorage is per-tab,
 *   cleared when the tab closes, never sent to a server.
 */

const STORAGE_KEYS = Object.freeze({
  memberId: 'officernd_memberId',
  memberName: 'officernd_memberName',
  token: 'officernd_token',
  // Per-tab replay-protection secret. Lives ONLY in sessionStorage (never the
  // URL), so a copied auto-login URL opened in another browser cannot present
  // it. The backend binds the token to this nonce on first use and rejects
  // reuse with a different/absent nonce. See AUTO_LOGIN_REPLAY_PROTECTION_PLAN.md.
  nonce: 'officernd_client_nonce',
});

const URL_PARAM_KEYS = Object.freeze(['memberId', 'memberName', 'token']);

/**
 * Returns OfficeRND auto-login params if a complete set is available,
 * either from the URL or from sessionStorage. If found in the URL, the
 * URL is scrubbed and the params are mirrored into sessionStorage.
 *
 * @returns {{memberId: string, memberName: string, token: string} | null}
 */
export function consumeOfficerndAutoLoginParams() {
  const urlParams = new URLSearchParams(window.location.search);
  const fromUrl = {
    memberId: urlParams.get('memberId'),
    memberName: urlParams.get('memberName'),
    token: urlParams.get('token'),
  };

  // The iframe contract requires both memberId and token to attempt
  // auto-login. memberName is optional — backend tolerates an empty
  // string — but if memberId/token are present the auto-login flow runs.
  const hasUrlPair = !!(fromUrl.memberId && fromUrl.token);

  if (hasUrlPair) {
    sessionStorage.setItem(STORAGE_KEYS.memberId, fromUrl.memberId);
    sessionStorage.setItem(STORAGE_KEYS.memberName, fromUrl.memberName || '');
    sessionStorage.setItem(STORAGE_KEYS.token, fromUrl.token);
    scrubUrl(urlParams);
    return {
      memberId: fromUrl.memberId,
      memberName: fromUrl.memberName || '',
      token: fromUrl.token,
      clientNonce: getOrCreateClientNonce(),
    };
  }

  const fromStorage = readFromSessionStorage();
  if (fromStorage) return fromStorage;

  return null;
}

/**
 * Cheap predicate for code paths that just need to know "are we in
 * auto-login mode?" without reading or scrubbing the token. Safe to call
 * during render.
 *
 * @returns {boolean}
 */
export function hasOfficerndAutoLoginParams() {
  const urlParams = new URLSearchParams(window.location.search);
  if (urlParams.get('memberId') && urlParams.get('token')) return true;
  return !!readFromSessionStorage();
}

/**
 * Clears the per-tab auto-login mirror. Call this on explicit logout if
 * you want to break the auto-login loop on next reload.
 */
export function clearOfficerndAutoLoginParams() {
  sessionStorage.removeItem(STORAGE_KEYS.memberId);
  sessionStorage.removeItem(STORAGE_KEYS.memberName);
  sessionStorage.removeItem(STORAGE_KEYS.token);
  sessionStorage.removeItem(STORAGE_KEYS.nonce);
}

// --- internals ---

function readFromSessionStorage() {
  const memberId = sessionStorage.getItem(STORAGE_KEYS.memberId);
  const token = sessionStorage.getItem(STORAGE_KEYS.token);
  if (!memberId || !token) return null;
  return {
    memberId,
    memberName: sessionStorage.getItem(STORAGE_KEYS.memberName) || '',
    token,
    // Same nonce created on first load — so a reload (this tab) re-presents it
    // and the backend recognises the same client.
    clientNonce: getOrCreateClientNonce(),
  };
}

/**
 * Returns this tab's auto-login nonce, creating + persisting it on first call.
 * sessionStorage is per-tab and survives reloads, so the real member's reload
 * re-uses the same nonce while another browser (copied URL) generates a new one.
 */
function getOrCreateClientNonce() {
  let nonce = sessionStorage.getItem(STORAGE_KEYS.nonce);
  if (!nonce) {
    nonce = generateNonce();
    sessionStorage.setItem(STORAGE_KEYS.nonce, nonce);
  }
  return nonce;
}

function generateNonce() {
  try {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    const arr = new Uint8Array(16);
    window.crypto.getRandomValues(arr);
    return Array.from(arr, (b) => b.toString(16).padStart(2, '0')).join('');
  } catch (e) {
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

function scrubUrl(urlParams) {
  URL_PARAM_KEYS.forEach((key) => urlParams.delete(key));
  const remaining = urlParams.toString();
  const newSearch = remaining ? `?${remaining}` : '';
  const newUrl = `${window.location.pathname}${newSearch}${window.location.hash}`;
  window.history.replaceState({}, '', newUrl);
}
